import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBars, FaTimes } from "react-icons/fa";
import { useCompany } from "../../hooks/useData";

const Navigation = () => {
  const company = useCompany();
  const location = useLocation();
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Professional Menu Order
  const navLinks = [
    { path: "/#products", label: "Products" },
    { path: "/products/school-pro/pricing", label: "Pricing 💰" },
    { path: "/tools", label: "Tools 🎁" },
    { path: "/case-studies/software-factory", label: "Case Study 🚀" },
    { path: "/blog", label: "Blog 📰" },
    { path: "/services", label: "Services" },
    { path: "/#contact", label: "Contact" },
  ];

  const handleNavClick = async (path, e) => {
    if (e) e.preventDefault();
    setIsMobileMenuOpen(false);

    if (path.startsWith("/#")) {
      const hash = path.split("#")[1];

      // If NOT on home page, navigate to home first
      if (location.pathname !== "/") {
        await navigate("/");
        // Small delay to allow home page to mount
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 500);
      } else {
        // If already on home page, just scroll
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    } else {
      // Normal navigation
      navigate(path);
    }
  };

  /* Install PWA Logic */
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setDeferredPrompt(null);
    }
  };

  return (
    <nav
      className={`sticky top-0 z-[999] transition-all duration-300 border-b border-transparent ${
        isScrolled
          ? "bg-white/80 backdrop-blur-md shadow-sm border-gray-200"
          : "bg-white/60 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 py-2 md:py-3 flex justify-between items-center">
        <Link
          to="/"
          className="text-2xl font-extrabold text-primary hover:text-secondary transition-colors"
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <img
            src="/muskan-logo.png"
            alt={company.name}
            className="h-16 md:h-20 w-auto object-contain"
            width="150"
            height="80"
          />
        </Link>

        {/* Desktop Menu */}
        <ul className="hidden md:flex items-center gap-8 m-0 p-0 list-none">
          {navLinks.map((link) => (
            <li key={link.path}>
              <a
                href={link.path}
                onClick={(e) => handleNavClick(link.path, e)}
                className={`relative text-textDark font-medium text-base transition-colors duration-300 hover:text-primary after:content-[''] after:absolute after:bottom-[-5px] after:left-0 after:w-0 after:h-[2px] after:bg-primary after:transition-[width] after:duration-300 hover:after:w-full ${
                  location.pathname === link.path
                    ? "text-primary after:w-full"
                    : ""
                }`}
              >
                {link.label}
              </a>
            </li>
          ))}
          {/* Install Button (Desktop) */}
          {deferredPrompt && (
            <li>
              <button
                onClick={handleInstallClick}
                className="ml-4 bg-primary text-white border-none py-2 px-6 rounded-full font-semibold cursor-pointer hover:bg-secondary transition-all duration-300 hover:shadow-lg transform hover:-translate-y-0.5"
              >
                Install App
              </button>
            </li>
          )}
        </ul>

        {/* Mobile Toggle */}
        <button
          className="md:hidden bg-transparent border-none text-2xl text-textDark cursor-pointer p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 overflow-hidden"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ul className="flex flex-col gap-0 m-0 p-4 list-none">
              {navLinks.map((link) => (
                <li
                  key={link.path}
                  className="border-b border-gray-100 last:border-none"
                >
                  <a
                    href={link.path}
                    onClick={(e) => handleNavClick(link.path, e)}
                    className={`block py-4 text-textDark font-medium text-lg transition-colors duration-300 hover:text-primary ${
                      location.pathname === link.path ? "text-primary" : ""
                    }`}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              {/* Install Button (Mobile) */}
              {deferredPrompt && (
                <li className="pt-4">
                  <button
                    onClick={() => {
                      handleInstallClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full bg-primary text-white border-none py-3 rounded-lg font-semibold cursor-pointer hover:bg-secondary transition-colors"
                  >
                    Install App
                  </button>
                </li>
              )}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navigation;
